import {takeUntil} from 'rxjs/operators';
import {Component, Input, OnInit} from '@angular/core';
import {SessionStorage} from 'ngx-webstorage';
import {NavigationStep} from '../../shared/guards/navigationStep-enum';
import {LoggerService} from '../../shared/logging/logger.service';
import {User} from '../../shared/user/user';
import {Item, Project, Status} from '../../project/shared/project-model';
import {TreeNode} from 'primeng/api';
import {BomHelper, BomService} from '../../bom/shared';
import {UtilService} from '../../shared/util/util.service';
import {BomTreeDataHelper} from '../../bom/shared/bom-tree-data-helper.service';
import {TranslateService} from '@ngx-translate/core';
import {ItemService} from '../../project/shared/item.service';
import {RightsService} from '../../shared/rights/rights.service';
import {ProjectService} from '../../project/shared/project.service';
import {ReferenceBom} from '../../bom/shared/bom-model';
import {Range, RangeStatus} from '../../shared/model/range-model';
import {Subject} from 'rxjs';
import {OfferService} from '../../offers/shared/offer.service';
import {Role} from '../../core/access-control/enum';
import {ViewCartBomEventDatalayerService} from '../../shared/google-analytics/view-cart-bom-event-datalayer.service';

@Component({
  standalone: false,
  selector: 'app-project-bom-page',
  templateUrl: './project-bom-page.component.html',
  styleUrls: ['./project-bom-page.component.less']
})
export class ProjectBomPageComponent implements OnInit {

  @SessionStorage()
  user: User;

  @SessionStorage()
  currentProject: Project;

  @SessionStorage()
  noItemNavigationStep;

  @SessionStorage()
  localization;

  @SessionStorage()
  isOnAdministrationScreen = false;

  @SessionStorage()
  isDemoMode: boolean;

  @SessionStorage()
  hasRegister: boolean;

  @SessionStorage()
  maxDeliveryTime;

  loadingBom = false;

  expandedNodes: string[] = [];

  cols: Array<any>;

  configuredQuotedTreeData: TreeNode[] = [];

  orderedTreeData: TreeNode[] = [];

  configuredQuotedStatusList: Status [] = [Status.configured, Status.quoted];
  configuredQuotedItemList: Item[];
  orderedStatusList: Status [] = [Status.ordered];
  orderedItemList: Item[];
  configuredQuotedTableTitle: string;
  orderedTableTitle: string;
  bomString: string = null;
  status = Status;
  ranges: Range[];

  unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private logger: LoggerService,
              private translateService: TranslateService,
              private itemService: ItemService,
              private rightsService: RightsService,
              private bomService: BomService,
              private bomHelper: BomHelper,
              private utilService: UtilService,
              private bomTreeDataHelper: BomTreeDataHelper,
              private projectService: ProjectService,
              private offerService: OfferService,
              private viewCartBomEventDatalayerService: ViewCartBomEventDatalayerService) {
  }

  private static createTreeNode(configuration, quotationDate, discount): TreeNode {
    return {
      data: {configuration: configuration, quotationDate: quotationDate, discount: discount},
      children: []
    };
  }

  ngOnInit() {
    this.logger.debug('Project Bom Page Component init()');
    this.loadingBom = true;

    this.offerService.getRangesBackOffice()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(ranges => {
        this.ranges = ranges;

        this.maxDeliveryTime = 0;
        this.noItemNavigationStep = NavigationStep.PROJECT_BILL_OF_MATERIALS;

        this.itemService.getItems(this.currentProject.id).subscribe(items => {
          this.getBomTreeDataFromProject(items);
          let refsArray: ReferenceBom[] = [];
          let totalPrice = 0;
          items.forEach(item => {
            totalPrice += UtilService.applyDiscountPrice(item.bom.totalPrice, item.discount);
            const refArray: ReferenceBom[] = this.bomService.extractReferencesToArray(item);
            refsArray = refsArray.concat(refArray);
          });
        });
      });
  }

  /**
   *
   * @param itemList
   */

  getBomTreeDataFromProject(itemList: Item[]) {
    this.loadingBom = true;
    this.getProjectBOMColumns(itemList);
    this.configuredQuotedItemList = itemList.filter(item =>
      this.configuredQuotedStatusList.includes(item.status) && (!!item.packageOfferId || item.range.status !== RangeStatus.DECOMMISSIONED));
    this.orderedItemList = itemList.filter(item => this.orderedStatusList.includes(item.status) && (!!item.packageOfferId || item.range.status !== RangeStatus.DECOMMISSIONED));
    if (this.configuredQuotedItemList !== null && this.configuredQuotedItemList.length > 0) {
      this.configuredQuotedTreeData = this.getBomTreeDataFromItem(this.configuredQuotedItemList);
      this.configuredQuotedTableTitle = this.translateService.instant('T_BOM_CONFIGURED_QUOTED_CONFIGURATIONS');
    }
    if (this.orderedItemList !== null && this.orderedItemList.length > 0) {
      this.orderedTreeData = this.getBomTreeDataFromItem(this.orderedItemList);
      this.orderedTableTitle = this.translateService.instant('T_BOM_ORDERED_CONFIGURATIONS');
    }
    this.loadingBom = false;
    this.logProjectBomInfo(itemList);
    this.projectService.getProject(this.currentProject.id).subscribe((updatedProject: Project) => {
      this.currentProject = updatedProject;
    });
  }

  /**
   * Return true if the user is a mySE user
   * @returns {boolean}
   */
  isMySEUser(): boolean {
    return !!this.user.currentMySEAccount;
  }

  /**
   * Return true if the project contains configured or quoted item
   */
  isProjectHasConfiguredQuotedItem(): boolean {
    return this.currentProject?.itemCT?.length > 0 &&
      this.currentProject.itemCT.some(item => this.configuredQuotedStatusList.includes(item.status));
  }

  /**
   * Return true if the project contains ordered item
   */
  isProjectHasOrderedItem(): boolean {
    return this.currentProject?.itemCT && this.currentProject.itemCT.length > 0 &&
      this.currentProject.itemCT.some(item => this.orderedStatusList.includes(item.status));
  }

  /**
   * Return true if the user is for a demo
   */
  isDemoUser(): boolean {
    return this.rightsService.isActivatedDemo();
  }

  /**
   * Return true if the user is a contractor
   */
  isContractor(): boolean {
    return this.rightsService.isContractor();
  }

  /**
   * Check if the project is for read-only
   */
  isReadOnlyProject(): boolean {
    return this.projectService.isReadOnlyProject(this.currentProject, this.user);
  }

  isCpqProject(): boolean {
    return this.projectService.isCpqProject(this.currentProject);
  }

  isCpqUser(): boolean {
    return this.user.role == Role.CPQ_USER;
  }

  /**
   * Check if the tree data has null public prices
   */
  bomContainsNullPublicPrices(): boolean {
    let bomContainsNullPrices = false;
    if (this.isProjectHasConfiguredQuotedItem()) {
      bomContainsNullPrices = this.hasNullPublicPrices(this.configuredQuotedTreeData);
    }
    if (!bomContainsNullPrices && this.isProjectHasOrderedItem()) {
      bomContainsNullPrices = this.hasNullPublicPrices(this.orderedTreeData);
    }
    return bomContainsNullPrices;
  }

  /**
   * Check if the tree data has null net prices
   */
  bomContainsNullNetPrices(): boolean {
    let bomContainsNullPrices = false;
    if (this.isProjectHasConfiguredQuotedItem()) {
      bomContainsNullPrices = this.hasNullNetPrices(this.configuredQuotedTreeData);
    }
    if (!bomContainsNullPrices && this.isProjectHasOrderedItem()) {
      bomContainsNullPrices = this.hasNullNetPrices(this.orderedTreeData);
    }
    return bomContainsNullPrices;
  }

  /**
   *
   * @param itemList
   * @private
   */
  private getBomTreeDataFromItem(itemList: Item[]): TreeNode[] {
    let treeData: TreeNode[] = [];
    itemList.forEach(item => {
      const myTreeData: TreeNode[] = this.bomTreeDataHelper.extractReferencesToTree(item, this.ranges);

      const rangeNode: TreeNode = ProjectBomPageComponent.createTreeNode(item.name, this.getQuotationDate(item), this.getDiscount(item));

      myTreeData.forEach(td => rangeNode.children.push(td));

      const myNewTreeData: TreeNode[] = [];
      myNewTreeData.push(rangeNode);

      myNewTreeData.forEach(value => {
        // restore expanded state
        value.expanded = true;

        this.bomHelper.formatTreeNode(value, this.isDemoMode);
        // Remove useless information
        value.children.forEach(child => child.data.netPrice = null);
      });
      treeData = treeData.concat(myNewTreeData);
    });
    return treeData;
  }

  /**
   * Get the project Bom columns
   */
  private getProjectBOMColumns(items: Item[]) {
    this.cols = this.bomHelper.getBOMColumns(this.isDemoMode,
      this.projectService.isCpqProject(this.currentProject),
      this.rightsService.canApplyDiscount(),
      this.isMySEUser(),
      this.someFoNetPricesValid(items),
      BomService.displayAvailability(items), this.rightsService.isPricesDetailsHidden());
    this.cols = this.cols.filter(col => col.header !== 'T_BOM_VALUE');
    this.cols.unshift({field: 'configuration', header: 'T_BOM_CONFIGURATION', className: 'text-left'});
  }

  public someFoNetPricesValid(items: Item[]) {
    return items.some(item => item?.totalPrice.validFoNetPrice);
  }

  /**
   * Get the quotation date for item
   * @param item
   */
  private getQuotationDate(item: Item): string {
    // Set locale date format
    const locale = this.user.preferredLanguage || 'en-US';
    // Locale date
    const localeDate = new Date(item.date).toLocaleDateString(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    // Validity date
    const futureDate = new Date(item.dateValidityQuotation);
    const validityDate = futureDate.toLocaleDateString(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    // Quotation date
    if (localeDate && validityDate && Status.quoted === item.status) {
      if (item.dateValidityQuotation > new Date().getTime()) {
        return this.translateService.instant('T_BOM_QUOTATION_DATE') + ': ' + localeDate + ', '
          + this.translateService.instant('T_BOM_QUOTATION_VALID') + ' ' + validityDate;
      } else {
        return this.translateService.instant('T_BOM_QUOTATION_EXPIRED') + ' ' + validityDate;
      }
    } else if (Status.configured === item.status) {
      return this.translateService.instant('T_BOM_NO_QUOTATION_ESTABLISHED');
    } else {
      return '';
    }
  }

  /**
   * Get the discount label for item
   * @param item
   */
  private getDiscount(item: Item): string {
    if (item.discount) {
      return this.translateService.instant('T_PROJECT_BOM_DISCOUNT_LABEL', {discount: item.discount});
    } else {
      return '';
    }
  }


  /**
   * Get project Bom infos
   * @param itemsProject
   */
  private logProjectBomInfo(itemsProject: Item []) {
    setTimeout(() => {
      let refsArray: ReferenceBom[] = [];
      itemsProject.forEach(item => {
        const refArray: ReferenceBom[] = this.bomService.extractReferencesToArray(item);
        refsArray = refsArray.concat(refArray);
      });
      const refDtoArray = refsArray.map(
        (referenceBom: ReferenceBom) =>
          'REFERENCE :' + referenceBom.ref + ' REFERENCE_DESCRIPTION : ' + referenceBom.description + ' QUANTITY ' + referenceBom.count);
      this.bomString = refDtoArray.toString();

      let totalPrice = 0;
      itemsProject.forEach(item => {
        totalPrice += UtilService.applyDiscountPrice(item.bom.totalPrice, item.discount);
      });

      this.viewCartBomEventDatalayerService.viewProjectBomEvent(totalPrice, this.currentProject, this.localization.currency, itemsProject, refsArray);
      const finalisePayload: any = {
        currency: this.localization.currency,
        bom_references: this.bomString
      };
      if (!this.rightsService.isActivatedDemo()) {
        finalisePayload.total_price = UtilService.formatPrice(totalPrice);
      }
      this.logger.business('Finalise configuration', finalisePayload);

    }, 1000);
  }

  /**
   * Check if the tree data has null public prices
   * @param treeData
   * @private
   */
  private hasNullPublicPrices(treeData: TreeNode[]): boolean {
    const isPricesDetailsHidden = this.rightsService.isPricesDetailsHidden();
    const canApplyDiscount = this.rightsService.canApplyDiscount();
    return treeData.some(treeNode =>
      treeNode.children.some(childTreeNode => {
        if (isPricesDetailsHidden) {
          const hasPublicNullPrices = this.hasNullPricesInChildren(childTreeNode.children);
          if ((childTreeNode.data.totalPrice === 0 || childTreeNode.data.totalPrice === 'containsNullPrices') && hasPublicNullPrices) {
            childTreeNode.data.totalPrice = 'containsNullPrices';
            return true;
          }
          return hasPublicNullPrices;
        }
        if (childTreeNode.data.totalPrice === 'containsNullPrices') {
          return canApplyDiscount ? this.hasNullPricesInChildren(childTreeNode.children) : true;
        }
        return false;
      }));
  }

  /**
   * Check if the tree data has null net prices
   * @param treeData
   * @private
   */
  private hasNullNetPrices(treeData: TreeNode[]): boolean {
    const isPricesDetailsHidden = this.rightsService.isPricesDetailsHidden();
    const canApplyDiscount = this.rightsService.canApplyDiscount();
    return treeData.some(treeNode =>
      treeNode.children.some(childTreeNode => {
        const isTotalNetPriceInvalid = childTreeNode.data.totalNetPrice === null || childTreeNode.data.totalNetPrice === 'containsNullPrices';
        if (isPricesDetailsHidden) {
          const hasNetNullPrices = this.checkChildNodeForNullPrices(childTreeNode.children, canApplyDiscount);
          if ((childTreeNode.data.totalNetPrice === 0 || childTreeNode.data.totalNetPrice === 'containsNullPrices') && hasNetNullPrices) {
            childTreeNode.data.totalNetPrice = 'containsNullPrices';
            return true;
          }
          return hasNetNullPrices;
        }
        if (isTotalNetPriceInvalid) {
          return canApplyDiscount ? this.hasNullPricesInChildren(childTreeNode.children) : true;
        }
        return false;
      }));
  }

  /**
   * Check if the children of a tree node have null prices
   * @param children
   * @private
   */
  private hasNullPricesInChildren(children: TreeNode[]) {
    const included = this.translateService.instant('T_BOM_INCLUDED');
    return children.some(childNode => {
      if (childNode.data.price === null) return true;
      if (childNode.data.price === included) return false;
      return childNode.children.some(grandChildNode => {
        if (grandChildNode.data.price === null) return true;
        if (grandChildNode.data.price === included) return false;
      });
    });
  }

  /**
   * Check if child node has null prices
   * @param children
   * @param canApplyDiscount
   * @private
   */
  private checkChildNodeForNullPrices(children: TreeNode[], canApplyDiscount: boolean): boolean {
    const included = this.translateService.instant('T_BOM_INCLUDED');
    const priceKey = canApplyDiscount ? 'price' : 'netPrice';
    return children.some(childNode => {
      if (childNode.data[priceKey] === null) return true;
      if (childNode.data[priceKey] === included) return false;
      return childNode.children.some(grandChildNode => {
        if (grandChildNode.data[priceKey] === null) return true;
        if (grandChildNode.data[priceKey] === included) return false;
      });
    });
  }
}
